import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Calendar, Clock, MapPin, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const RSVPPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRSVP = async () => {
      try {
        const url = action
          ? `${API}/api/rsvp/${token}?action=${action}`
          : `${API}/api/rsvp/${token}`;
        const res = await axios.get(url);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Invitation not found");
      } finally {
        setLoading(false);
      }
    };
    fetchRSVP();
  }, [token, action]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border p-8 max-w-md text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Invitation Not Found</h1>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const tryout = data?.tryout || {};
  const status = data?.status || data?.invite?.status;
  const isAccepted = status === "accepted";
  const isDeclined = status === "declined";
  const responded = isAccepted || isDeclined;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="rsvp-page">
      <div className="bg-white rounded-xl shadow-lg border max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-[#0C2E3C] px-6 py-8 text-center">
          <h1 className="text-2xl font-bold text-[#C8E64E] tracking-wide">TRYOUT INVITATION</h1>
          {data?.lead_name && (
            <p className="text-white/70 mt-1 text-sm">Welcome, {data.lead_name || data.invite?.lead_name}</p>
          )}
        </div>

        {/* Tryout Details */}
        <div className="px-6 py-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">{tryout.title}</h2>
          <div className="space-y-3">
            {tryout.date && (
              <div className="flex items-center gap-3 text-gray-600">
                <Calendar size={18} className="text-[#0C2E3C] shrink-0" />
                <span>{tryout.date}</span>
              </div>
            )}
            {tryout.time && (
              <div className="flex items-center gap-3 text-gray-600">
                <Clock size={18} className="text-[#0C2E3C] shrink-0" />
                <span>{tryout.time}</span>
              </div>
            )}
            {tryout.location && (
              <div className="flex items-center gap-3 text-gray-600">
                <MapPin size={18} className="text-[#0C2E3C] shrink-0" />
                <span>{tryout.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* Status / Actions */}
        <div className="px-6 pb-8">
          {responded ? (
            <div className={`rounded-lg p-5 text-center ${isAccepted ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
              {isAccepted ? (
                <>
                  <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                  <h3 className="font-bold text-green-800 text-lg">You&apos;re In!</h3>
                  <p className="text-green-600 text-sm mt-1">{data?.message || "We look forward to seeing you at tryouts."}</p>
                </>
              ) : (
                <>
                  <XCircle className="w-10 h-10 text-red-400 mx-auto mb-2" />
                  <h3 className="font-bold text-red-800 text-lg">Invitation Declined</h3>
                  <p className="text-red-600 text-sm mt-1">{data?.message || "Thank you for letting us know."}</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-gray-600 text-sm text-center mb-4">Please let us know if you can make it:</p>
              <div className="flex gap-3">
                <a
                  href={`${API}/api/rsvp/${token}?action=accept`}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white text-center py-3 rounded-lg font-bold transition-colors"
                  data-testid="rsvp-accept"
                >
                  I&apos;ll Be There
                </a>
                <a
                  href={`${API}/api/rsvp/${token}?action=decline`}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white text-center py-3 rounded-lg font-bold transition-colors"
                  data-testid="rsvp-decline"
                >
                  Can&apos;t Make It
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RSVPPage;
